import type { ReactNode } from "react";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
  type Table as TanstackTable,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/base/table";
import { DataTablePagination } from "./DataTablePagination";

/** Configuration for server-side (manual) pagination. */
interface ManualPaginationConfig {
  pageIndex: number;
  pageSize: number;
  /**
   * Total pages known so far. Use -1 for unknown (hides "of N" page label
   * and keeps next-page button enabled while `hasNextPage` allows).
   */
  pageCount: number;
  onPaginationChange: (pagination: PaginationState) => void;
  /** Total row count for display. Pass `null` to hide the count (cursor pagination). */
  totalRows?: number | null;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  initialSorting?: SortingState;
  onRowClick?: (row: TData) => void;
  toolbar?: (table: TanstackTable<TData>) => ReactNode;
  /** Enable server-side pagination. When set, `data` is the current page only. */
  manualPagination?: ManualPaginationConfig;
  /**
   * Custom footer rendered in place of the built-in page-number pagination.
   * When provided, client-side pagination is disabled and all rows in `data`
   * render as a single page (use for cursor-based server pagination).
   */
  footer?: ReactNode;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  initialSorting = [],
  onRowClick,
  toolbar,
  manualPagination: mp,
  footer,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const table = useReactTable(
    mp
      ? {
          data,
          columns,
          getCoreRowModel: getCoreRowModel(),
          // No getSortedRowModel — sorting is handled by the BLoC on the
          // full dataset before slicing pages. Client-side sort would only
          // reorder the current page's items.
          getFilteredRowModel: getFilteredRowModel(),
          getFacetedRowModel: getFacetedRowModel(),
          getFacetedUniqueValues: getFacetedUniqueValues(),
          onColumnFiltersChange: setColumnFilters,
          manualPagination: true,
          pageCount: mp.pageCount,
          state: {
            columnFilters,
            pagination: { pageIndex: mp.pageIndex, pageSize: mp.pageSize },
          },
          onPaginationChange: (updater) => {
            const current = { pageIndex: mp.pageIndex, pageSize: mp.pageSize };
            const next =
              typeof updater === "function" ? updater(current) : updater;
            mp.onPaginationChange(next);
          },
        }
      : {
          data,
          columns,
          getCoreRowModel: getCoreRowModel(),
          // When a custom footer is supplied, pagination AND ordering are
          // handled by the server (cursor-based). Render all current-page rows
          // as one page in their server-provided order — omitting the
          // pagination/sorted row models prevents client-side slicing and stops
          // a header click from re-sorting only the current page's items.
          ...(footer
            ? {}
            : {
                getPaginationRowModel: getPaginationRowModel(),
                getSortedRowModel: getSortedRowModel(),
                onSortingChange: setSorting,
              }),
          getFilteredRowModel: getFilteredRowModel(),
          getFacetedRowModel: getFacetedRowModel(),
          getFacetedUniqueValues: getFacetedUniqueValues(),
          onColumnFiltersChange: setColumnFilters,
          state: footer ? { columnFilters } : { sorting, columnFilters },
          ...(footer ? {} : { initialState: { pagination: { pageSize: 10 } } }),
        },
  );

  return (
    <div className="space-y-4">
      {toolbar?.(table)}
      <div className="bg-card overflow-x-auto rounded-lg border">
        <Table className="w-full min-w-max">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-muted">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="text-muted-foreground text-xs font-medium tracking-wide uppercase"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={
                    onRowClick ? "hover:bg-muted cursor-pointer" : undefined
                  }
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-subtle-foreground py-8 text-center text-sm"
                >
                  No results found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {footer ?? (
          <DataTablePagination table={table} totalRows={mp?.totalRows} />
        )}
      </div>
    </div>
  );
}
